import { Fragment, useCallback, useState } from 'react';
import { requestPresignedDownload } from '../../../../api/files';
import { formatBytes } from '../../../../lib/attachments';
import { fileIconFor, fileIconClass } from '../../../../lib/file-icon';
import { attachmentPreviewDoc } from '../../../../lib/preview-doc';
import { displayNameOf, splitHighlight, type DatedAttachment } from '../../../../lib/attachment-groups';
import { useChatPreviewStore } from '../../../../store/chatPreviewStore';
import { Download } from '../../../../icons';
// chat-attachment-save — 여기서도 어디에 둘지 고를 수 있어야 한다(파일 카드와 같은 버튼).
import { AttachmentSaveMenu } from '../../../../components/Attachment/AttachmentSaveMenu';
import { tChat } from '../../../../lib/chat-i18n';
import { PreviewUnavailableModal } from '../PreviewUnavailableModal';
import { formatMessageTime } from '../utils/formatMessageTime';

/**
 * 미디어 브라우저 파일 행 (chat-media-date-groups — 2026-08-07).
 *
 * 기존 `ChatMediaPanel` 의 `MediaFileRow` 와 **동일한 동작**을 유지한다 — 본문 클릭 → 미리보기
 * 가능하면 우측 패널, 아니면 '미리보기 불가' 경고 모달, 다운로드는 오른쪽 버튼. 재사용하는 것도
 * 같은 것들(`attachmentPreviewDoc` · `useChatPreviewStore` · `PreviewUnavailableModal` ·
 * `fileIconFor/fileIconClass` · `requestPresignedDownload`)이라 클릭 이후 경로는 한 줄도 안 바뀐다.
 *
 * 추가된 것은 두 가지뿐이다:
 *   1) 검색어 하이라이트 (`<mark>` — dangerouslySetInnerHTML 미사용)
 *   2) 크기 옆 **시각 · 보낸사람** 메타 — '메시지로 이동' 없이 "누가 언제 올렸나" 를 답한다.
 *
 * CSS 는 기존 `.chat-media-file*` 클래스를 그대로 쓴다(펼친 일자 안쪽은 오늘과 픽셀 동일).
 */

/** 검색어 매칭 구간만 `<mark>` 로 감싼다. 토큰이 없으면 통짜 텍스트. */
export function HighlightedText({ text, tokens }: { text: string; tokens: readonly string[] }) {
  const parts = splitHighlight(text, tokens);
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <mark key={i} className="chat-media-hit">
            {p.t}
          </mark>
        ) : (
          <Fragment key={i}>{p.t}</Fragment>
        ),
      )}
    </>
  );
}

interface MediaFileRowItemProps {
  attachment: DatedAttachment;
  roomId: string;
  tokens: readonly string[];
  senderName: string | null;
}

export function MediaFileRowItem({ attachment, roomId, tokens, senderName }: MediaFileRowItemProps) {
  // 진행 표시는 공용 저장 버튼이 쥔다 — 여기서는 중복 클릭만 막는다.
  const [, setBusy] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const openPreview = useChatPreviewStore((s) => s.openPreview);
  const FileIcon = fileIconFor({ name: attachment.name, mimeType: attachment.mimeType });
  const name = displayNameOf(attachment);

  const handlePreviewClick = useCallback(() => {
    const doc = attachmentPreviewDoc(attachment);
    if (doc) openPreview(roomId, doc);
    else setWarnOpen(true);
  }, [attachment, roomId, openPreview]);

  const handleDownload = useCallback(async () => {
    setBusy(true);
    try {
      const res = await requestPresignedDownload(attachment.fileId);
      const a = document.createElement('a');
      a.href = res.downloadUrl;
      a.download = res.name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      /* 다운로드 실패 — 조용히 무시(사용자 재시도 가능) */
    } finally {
      setBusy(false);
    }
  }, [attachment.fileId]);

  // 크기 · 시각 · 보낸사람 — 없는 조각은 자연히 빠진다(첨부 메타는 보장되지 않는다).
  const time = attachment.createdAt ? formatMessageTime(attachment.createdAt) : null;
  const meta = [formatBytes(attachment.sizeBytes ?? 0), time, senderName].filter(Boolean).join(' · ');

  return (
    <li className="chat-media-file">
      <button
        type="button"
        className="chat-media-file-btn"
        onClick={handlePreviewClick}
        aria-label={tChat('chat.media.preview_open_aria', { name })}
        title={`${name}\n${meta}`}
      >
        <span className={`chat-media-file-icon ${fileIconClass({ name: attachment.name, mimeType: attachment.mimeType })}`}>
          <FileIcon size={16} aria-hidden />
        </span>
        <span className="chat-media-file-meta">
          <span className="chat-media-file-name">
            <HighlightedText text={name} tokens={tokens} />
          </span>
          <span className="chat-media-file-size">{meta}</span>
        </span>
      </button>
      <AttachmentSaveMenu
        roomId={roomId}
        fileId={attachment.fileId}
        onDownload={handleDownload}
        className="chat-media-file-dl-btn"
        buttonLabel={tChat('chat.media.download_aria', { name })}
      >
        <Download size={14} aria-hidden />
      </AttachmentSaveMenu>
      {warnOpen && <PreviewUnavailableModal name={name} onClose={() => setWarnOpen(false)} />}
    </li>
  );
}
